import type { Page } from './feature';
import type { AuthRole } from '../shared/types';

/**
 * Roteador por hash: `#/sorteos/abc123` → página `/sorteos/:id` com `{ id: 'abc123' }`.
 * Hash porque o front é servido como estático (sem fallback no servidor) e as rotas de
 * autenticação já usam o mesmo esquema.
 */
export interface RouteMatch {
  page: Page;
  params: Record<string, string>;
}

export const DEFAULT_PATH = '/inicio';

/** '#/sorteos/abc?x=1' → '/sorteos/abc' */
export function currentPath(hash: string = location.hash): string {
  const raw = hash.replace(/^#/, '').split('?')[0] ?? '';
  return raw.startsWith('/') ? raw : '/' + raw;
}

/** '#/misiones?edit=abc' → { edit: 'abc' } */
export function currentQuery(hash: string = location.hash): Record<string, string> {
  const qs = hash.split('?')[1] ?? '';
  return Object.fromEntries(new URLSearchParams(qs).entries());
}

export const href = (path: string): string => '#' + path;

export function navigate(path: string, replace = false): void {
  if (replace) history.replaceState(null, '', location.pathname + location.search + href(path));
  else location.hash = path;
  if (replace) window.dispatchEvent(new HashChangeEvent('hashchange'));
}

export function matchPage(pages: Page[], path: string): RouteMatch | null {
  const parts = path.split('/').filter(Boolean);
  for (const page of pages) {
    const pat = page.pattern.split('/').filter(Boolean);
    if (pat.length !== parts.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < pat.length; i++) {
      const p = pat[i] as string, v = parts[i] as string;
      if (p.startsWith(':')) params[p.slice(1)] = decodeURIComponent(v);
      else if (p !== v) { ok = false; break; }
    }
    if (ok) return { page, params };
  }
  return null;
}

export const pagesFor = (pages: Page[], role: AuthRole): Page[] => pages.filter(p => p.roles.includes(role));
